/**
 * API Client Service - Dashboard API Requests
 * 
 * Author: Iswe Solutions Team
 * 
 * Provides typed API client methods for fetching dashboard data from the backend.
 * Includes JWT authentication for all requests.
 * Handles session expiration and redirects to login.
 * 
 * IMPORTANT: API Base URL
 * ========================
 * The backend uses `/api` prefix (NOT `/api/v1`).
 * All endpoints are relative to this base URL.
 * 
 * See: endpoints.ts for centralized endpoint definitions
 * See: backend/app/main.py for route registration
 */

import { API_BASE_URL } from './endpoints';

// Suppress unhandled AbortError rejections that leak from browser internals
// when requests are cancelled via AbortController timeout
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (e) => {
    if (e.reason && typeof e.reason === 'object' && (e.reason as { name?: string }).name === 'AbortError') {
      e.preventDefault();
    }
  });
}
// Re-export endpoints for convenience
export * from './endpoints';

interface ApiResponse<T> {
  data: T;
}

/**
 * Clear all authentication data from localStorage
 */
const clearAuthData = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('auth_method');
  // Clear server-side httpOnly cookie (fire-and-forget)
  fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
};

const rememberPostLoginPath = () => {
  const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (path && path !== '/login') {
    localStorage.setItem('post_login_redirect', path);
  }
};

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }


  private isAbortError(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const e = err as { name?: string };
    return e.name === 'AbortError';
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120s timeout for high-latency connection
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        credentials: 'include',
        signal: controller.signal,
        ...options,
      });
    } catch (fetchErr: unknown) {
      clearTimeout(timeoutId);
      if (this.isAbortError(fetchErr)) {
        throw new Error('Request timed out. Please try again.');
      }
      throw fetchErr;
    }
    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized - clear auth and redirect to login
      if (response.status === 401) {
        clearAuthData();
        rememberPostLoginPath();
        // Redirect to login page
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      // Handle 403 Forbidden - user is authenticated but lacks permission
      // Do NOT logout — user's token is valid, they just can't access this resource
      if (response.status === 403) {
        throw new Error('You do not have permission to access this resource.');
      }
      
      // Try to parse error response body for detailed error info
      let errorMessage = `API Error: ${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) {
          // Handle structured error from FastAPI (direct, no middleware)
          if (typeof errorBody.detail === 'object') {
            errorMessage = errorBody.detail.message || errorBody.detail.error || JSON.stringify(errorBody.detail);
          } else {
            errorMessage = errorBody.detail;
          }
        } else if (errorBody.error?.message) {
          // Handle wrapped error from exception middleware
          errorMessage = errorBody.error.message;
        } else if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // If we can't parse the error body, use the status text
      }
      
      throw new Error(errorMessage);
    }

    // Parse JSON response
    let data: T;
    try {
      data = await response.json();
    } catch {
      // Failed to parse JSON - could be empty response or server error HTML
      // Only treat as auth error if status was 200 (indicates login page redirect)
      if (response.status === 200) {
        // 200 OK but not JSON = likely redirected to HTML login page
        clearAuthData();
        rememberPostLoginPath();
        window.location.href = '/login';
        throw new Error('Invalid session. Redirecting to login...');
      }
      // For other statuses (e.g., 502 during deploy), throw as network error
      throw new Error(`Server returned non-JSON response (${response.status})`);
    }
    return { data };
  }

  // Dashboard endpoints
  async getDataQualityDashboard() {
    return this.request('/dashboards/data-quality');
  }

  async getExtractionDashboard() {
    return this.request('/dashboards/extraction');
  }

  async getMonitoringDashboard() {
    return this.request('/dashboards/monitoring');
  }

  async getPipelineDashboard() {
    return this.request('/dashboards/pipeline');
  }

  async getSystemHealthDashboard() {
    return this.request('/dashboards/system-health');
  }

  async getContainersDashboard() {
    return this.request('/dashboards/containers');
  }

  async getErrorsDashboard() {
    return this.request('/dashboards/errors');
  }

  // Analytics endpoint (used by EnhancedDashboard)
  async getAnalyticsSummary() {
    return this.request('/analytics/summary');
  }

  // Generic GET request - returns ApiResponse with data property
  async get<T>(endpoint: string, options?: { params?: Record<string, string | number> }): Promise<ApiResponse<T>> {
    let url = endpoint;
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString;
      }
    }
    return this.request<T>(url);
  }

  // Generic POST request
  async post<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // POST request with FormData (for file uploads)
  async postFormData<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
    // Note: Don't set Content-Type header for FormData - browser will set it with boundary

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      // Only logout on 401 (invalid/expired token), not 403 (insufficient permissions)
      if (response.status === 401) {
        clearAuthData();
        rememberPostLoginPath();
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }
      
      // 403 = authenticated but lacks permission — do NOT logout
      if (response.status === 403) {
        let detail = 'You do not have permission to perform this action.';
        try {
          const errorBody = await response.json();
          if (errorBody.detail) detail = typeof errorBody.detail === 'string' ? errorBody.detail : JSON.stringify(errorBody.detail);
        } catch { /* use default message */ }
        throw new Error(detail);
      }
      
      let errorMessage = `API Error: ${response.status}`;
      try {
        const errorBody = await response.json();
        if (errorBody.detail) {
          errorMessage = typeof errorBody.detail === 'string' ? errorBody.detail : JSON.stringify(errorBody.detail);
        }
      } catch {
        // Use status text if JSON parsing fails (e.g., 502 HTML during deploy)
      }
      throw new Error(errorMessage);
    }

    // Parse JSON response with non-JSON fallback
    let data: T;
    try {
      data = await response.json();
    } catch {
      // Failed to parse JSON - could be empty response or server error HTML
      if (response.status === 200) {
        // 200 OK but not JSON = likely redirected to HTML login page
        clearAuthData();
        rememberPostLoginPath();
        window.location.href = '/login';
        throw new Error('Invalid session. Redirecting to login...');
      }
      throw new Error(`Server returned non-JSON response (${response.status})`);
    }
    return { data };
  }

  // Generic PUT request
  async put<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Generic PATCH request
  async patch<T>(endpoint: string, data: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Generic DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for testing or custom instances
export { ApiClient };
